import React, { useState, useEffect, useRef } from 'react';

/**
 * Adres-autocomplete met PDOK Locatieserver (NL-only).
 * Roept onSelected aan met {adres, postcode, plaats} bij een match.
 */
export default function AdresAutocomplete({ value, onChange, onSelected, placeholder }) {
  const [suggesties, setSuggesties] = useState([]);
  const [open, setOpen] = useState(false);
  const [actief, setActief] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.trim().length < 3) {
      setSuggesties([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const url = `https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest?q=${encodeURIComponent(value)}&fq=type:adres&fq=postcode:[*+TO+*]&rows=8`;
        const r = await fetch(url);
        const data = await r.json();
        const docs = data?.response?.docs || [];
        setSuggesties(docs);
        setOpen(docs.length > 0);
        setActief(-1);
      } catch (e) {
        setSuggesties([]);
        setOpen(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  useEffect(() => {
    function handleClickBuiten(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickBuiten);
    return () => document.removeEventListener('mousedown', handleClickBuiten);
  }, []);

  const kiesSuggestie = async (sug) => {
    setOpen(false);
    setSuggesties([]);
    try {
      const url = `https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup?id=${encodeURIComponent(sug.id)}`;
      const r = await fetch(url);
      const data = await r.json();
      const doc = data?.response?.docs?.[0];
      if (!doc) return;
      const adresRegel = `${doc.straatnaam} ${doc.huisnummer}${doc.huisnummer_toevoeging ? ' ' + doc.huisnummer_toevoeging : ''}${doc.huisletter ? doc.huisletter : ''}`.trim();
      onSelected({
        adres: adresRegel,
        postcode: doc.postcode || '',
        plaats: doc.woonplaatsnaam || '',
      });
    } catch (e) {}
  };

  const onKeyDown = (e) => {
    if (!open || suggesties.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActief((i) => Math.min(i + 1, suggesties.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActief((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && actief >= 0) {
      e.preventDefault();
      kiesSuggestie(suggesties[actief]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggesties.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || 'Begin met typen, bv. Bavelselaan 57'}
        autoComplete="off"
      />
      {open && suggesties.length > 0 && (
        <ul
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            marginTop: '4px',
            padding: '4px 0',
            listStyle: 'none',
            maxHeight: '320px',
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          {suggesties.map((sug, i) => (
            <li
              key={sug.id}
              onClick={() => kiesSuggestie(sug)}
              onMouseEnter={() => setActief(i)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                background: i === actief ? 'var(--bg-hover, #f5f5f7)' : 'transparent',
                fontSize: '14px',
              }}
            >
              {sug.weergavenaam}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
